import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftOutlined,
  CheckOutlined,
  EyeOutlined,
  MailOutlined,
  SendOutlined,
  TeamOutlined,
  WarningOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import { Button } from "antd";
import { audienceDetailRows } from "./audienceFlowData";
import { ChannelBadge, DetailStatCard } from "./AudienceFlowShared";
import FeatureComingSoon from "../../../common/FeatureComingSoon";

export const AudienceSuccessScreen = ({ audienceName, totalContacts = 2859, onFinish, onViewDetail, selectedSource }) => {
  const navigate = useNavigate();
  return (
  <div className="tw-mx-auto tw-flex tw-min-h-[70vh] tw-max-w-[760px] tw-flex-col tw-items-center tw-justify-center tw-px-4 tw-text-center">
    <div className="tw-relative tw-flex tw-h-24 tw-w-24 tw-items-center tw-justify-center tw-rounded-full tw-border-[4px] tw-border-solid tw-border-[#5D44FF]/20 tw-text-[#5D44FF]">
      <div className="tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-full tw-bg-gradient-to-tr tw-from-[#5D44FF] tw-to-[#8B70FF] tw-text-white tw-shadow-[0_10px_24px_rgba(93,68,255,0.25)]">
        <CheckOutlined className="tw-text-[28px] tw-stroke-[2.5]" />
      </div>
    </div>

    <h2 className="tw-m-0 tw-mt-8 tw-text-[30px] tw-font-bold tw-leading-tight tw-text-slate-800 tw-font-poppins">
      Audience Created Successfully
    </h2>
    <p className="tw-m-0 tw-mt-3.5 tw-text-[16px] tw-font-medium tw-text-slate-500 tw-font-inter tw-leading-relaxed">
      {selectedSource === "csv" ? (
        <>
          <span className="tw-font-bold tw-text-[#5D44FF]">{audienceName}</span> is importing{" "}
          <span className="tw-font-bold tw-text-slate-800">{totalContacts} Contacts</span>.
          <br/>
          <span className="tw-text-[13.5px] tw-mt-1 tw-block tw-text-slate-400">The CSV upload is in progress in the background. This may take a few minutes.</span>
        </>
      ) : (
        <>
          <span className="tw-font-bold tw-text-[#5D44FF]">{audienceName}</span> is ready with{" "}
          <span className="tw-font-bold tw-text-slate-800">{totalContacts} Contacts</span>
        </>
      )}
    </p>

    <div className="tw-mt-8 tw-flex tw-flex-wrap tw-justify-center tw-gap-3.5">
      <Button
        type="primary"
        onClick={() => navigate("/whatsapp-campaigns")}
        className="!tw-h-[42px] !tw-rounded-xl !tw-border-0 !tw-bg-[#16A34A] hover:!tw-bg-[#15803D] !tw-px-6 !tw-text-[13.5px] !tw-font-bold !tw-font-inter !tw-shadow-[0_4px_12px_rgba(22,163,74,0.12)]"
      >
        <WhatsAppOutlined />
        WhatsApp Campaign
      </Button>
      <FeatureComingSoon className="tw-inline-block">
        <Button 
          className="!tw-h-[42px] !tw-rounded-xl !tw-border-slate-200 !tw-bg-white hover:!tw-border-[#5D44FF] !tw-px-6 !tw-text-[13.5px] !tw-font-bold !tw-text-[#5D44FF] !tw-font-inter"
        >
          <MailOutlined />
          Email Campaign
        </Button>
      </FeatureComingSoon>
      <Button
        onClick={onViewDetail}
        className="!tw-h-[42px] !tw-rounded-xl !tw-border-slate-200 !tw-bg-white hover:!tw-bg-slate-50 !tw-px-6 !tw-text-[13.5px] !tw-font-bold !tw-text-slate-650 !tw-font-inter"
      >
        <EyeOutlined />
        View Audience
      </Button>
    </div>

    <button
      type="button"
      onClick={onFinish}
      className="tw-mt-8 tw-border-0 tw-bg-transparent tw-text-[14px] tw-font-normal tw-text-black tw-flex tw-items-center"
    >
      <ArrowLeftOutlined className="tw-mr-2" />
      Back to Audiences
    </button>
  </div>
  );
};

export const AudienceDetailScreen = ({ stats, displayRows, onBackToListing }) => {
  const finalStats = stats || {
    total: 2859,
    whatsapp: 895,
    email: 1000,
    invalid: 0,
  };
  const finalRows = displayRows && displayRows.length > 0 ? displayRows : audienceDetailRows;

  // Helper for progress bar width
  const getPercentWidth = (val) => {
    if (finalStats.total === 0) return "0%";
    const pct = Math.min(100, Math.round((val / finalStats.total) * 100));
    return `${pct}%`;
  };

  return (
    <div className="tw-mx-auto tw-max-w-[1140px] tw-px-4 tw-py-6">
      <div className="tw-mb-6 tw-flex tw-items-center tw-justify-between tw-gap-4">
        <button
          type="button"
          onClick={onBackToListing}
          className="tw-border-0 tw-bg-transparent tw-text-[13.5px] tw-font-bold tw-text-[#5D44FF] hover:tw-opacity-80 tw-flex tw-items-center tw-font-inter tw-transition-opacity tw-cursor-pointer"
        >
          <ArrowLeftOutlined className="tw-mr-2" />
          Back to Audiences
        </button>

        <FeatureComingSoon className="tw-inline-block">
          <Button
            type="primary"
            className="!tw-h-[42px] !tw-rounded-xl !tw-border-0 !tw-bg-[#5D44FF] hover:!tw-bg-[#4F36F2] !tw-px-6 !tw-text-[13.5px] !tw-font-bold !tw-shadow-[0_4px_12px_rgba(93,68,255,0.15)] !tw-font-inter"
          >
            <SendOutlined />
            Send Campaign
          </Button>
        </FeatureComingSoon>
      </div>

      <div className="tw-grid tw-grid-cols-1 tw-gap-6 md:tw-grid-cols-2 xl:tw-grid-cols-4">
        <DetailStatCard
          label="Total Contacts"
          value={String(finalStats.total)}
          progressWidth={getPercentWidth(finalStats.total)}
          iconWrapClass="tw-bg-[#EBE8FD] tw-text-[#5D44FF]"
          icon={<TeamOutlined />}
          progressClass="tw-bg-[#5D44FF]"
          progressTrackClass="tw-bg-[#EBE8FD]"
        />
        <DetailStatCard
          label="Whatsapp ready"
          value={String(finalStats.whatsapp)}
          progressWidth={getPercentWidth(finalStats.whatsapp)}
          iconWrapClass="tw-bg-[#EAFBF0] tw-text-[#22C55E]"
          icon={<WhatsAppOutlined />}
          progressClass="tw-bg-[#22C55E]"
          progressTrackClass="tw-bg-[#EAFBF0]"
        />
        <DetailStatCard
          label="Email ready"
          value={String(finalStats.email)}
          progressWidth={getPercentWidth(finalStats.email)}
          iconWrapClass="tw-bg-[#EBE8FD] tw-text-[#5D44FF]"
          icon={<MailOutlined />}
          progressClass="tw-bg-[#5D44FF]"
          progressTrackClass="tw-bg-[#EBE8FD]"
        />
        <DetailStatCard
          label="Invalid"
          value={String(finalStats.invalid)}
          progressWidth={getPercentWidth(finalStats.invalid)}
          iconWrapClass="tw-bg-[#FFF1ED] tw-text-[#FF7A59]"
          icon={<WarningOutlined />}
          progressClass="tw-bg-[#FF7A59]"
          progressTrackClass="tw-bg-[#FFF1ED]"
        />
      </div>

      <div className="tw-mt-6 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-slate-100 tw-bg-white tw-shadow-[0_10px_30px_rgba(0,0,0,0.02)]">
        <div className="tw-grid tw-grid-cols-[1.3fr_1.25fr_1.3fr_1.2fr] tw-items-center tw-gap-4 tw-bg-slate-50 tw-px-5 tw-py-4 tw-text-[11px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-font-poppins tw-border-0 tw-border-b tw-border-solid tw-border-slate-100">
          <div>Name</div>
          <div>Phone</div>
          <div>Email</div>
          <div>Channels</div>
        </div>

        <div className="tw-divide-y tw-divide-solid tw-divide-slate-100">
          {finalRows.map((row) => (
            <div
              key={row.id}
              className="tw-grid tw-grid-cols-[1.3fr_1.25fr_1.3fr_1.2fr] tw-items-center tw-gap-4 tw-px-5 tw-py-4 tw-text-[13.5px] tw-text-slate-700 tw-font-medium hover:tw-bg-slate-50/20 tw-transition-colors"
            >
              <div className="tw-text-[13.5px] tw-font-bold tw-text-slate-800 tw-font-poppins">{row.name}</div>
              <div className="tw-font-medium tw-font-inter tw-text-slate-600">{row.phone}</div>
              <div className="tw-break-all tw-font-medium tw-font-inter tw-text-slate-650">{row.email}</div>
              <div className="tw-flex tw-flex-wrap tw-gap-2">
                {row.channels?.map((channel) => (
                  <ChannelBadge key={channel} type={channel} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
